import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  EditService,
  GridComponent,
  GridModule,
  IEditCell,
  PageService,
  ToolbarService
} from '@syncfusion/ej2-angular-grids';
import { SpinnerComponent } from '../shared/spinner.component';
import { ToastComponent } from '../shared/toast.component';
import { Observable } from 'rxjs';
import { TdRate, TdRateToAdd, TdRateToUpdate } from './td-rate.model';
import { TdRatesService } from './td-rates.service';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ApiErrorDialogComponent } from '../shared/api-error-dialog.component';
import { TdRatesBanksService } from './td-rates-banks.service';
import { TdRatesFiduciariesService } from './td-rates-fiduciaries.service';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { TdRateBank } from './td-rate-bank.model';
import { TdRateFiduciary } from './td-rate-fiduciary.model';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { FormBuilder, FormControl, FormGroup, FormsModule, Validators } from '@angular/forms';
import { DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import saveAs from 'file-saver';
import { UploaderComponent, UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { EaccessAuthService } from '../auth/eaccess-auth.service';
import { UploadPath } from '../shared/upload-path.model';
import { environment } from '../../environments/environment';
import { TD_RATES_CURRENCIES } from '../shared/constants';

@Component({
  selector: 'eaa-td-rates',
  imports: [
    AsyncPipe,
    DatePipe,
    DatePickerModule,
    DropDownListModule,
    FormsModule,
    GridModule,
    SpinnerComponent,
    ToastComponent,
    UploaderModule
  ],
  providers: [ToolbarService, EditService, PageService],
  template: `
    <div class="d-flex flex-column gap-2">
      <div class="d-flex justify-content-between gap-3">
        <div class="card flex-fill">
          <div class="card-header">
            <h4 class="card-title m-0">TD RATES</h4>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-center gap-3">
              <ejs-dropdownlist
                [dataSource]="currencies"
                [(ngModel)]="selectedCurrency"
                placeholder="Select currency"
                floatLabelType="Auto"
                (valueChange)="onCurrencyChange($event)"
              />
              <ejs-datepicker
                [(ngModel)]="selectedDate"
                placeholder="Select date"
                floatLabelType="Auto"
                [value]="selectedDate"
                (valueChange)="onDateChange($event)"
                format="dd/MM/yyyy"
                [serverTimezoneOffset]="0"
                [showClearButton]="false"
              />
            </div>
          </div>
        </div>
        <div class="card flex-fill">
          <div class="card-header">
            <h5 class="card-title mx-0 my-1">BULK UPLOAD</h5>
          </div>
          <div class="card-body d-flex gap-3 align-items-center justify-content-between gap-3">
            <div class="d-flex align-items-center gap-3">
              <div style="width: 300px">
                <ejs-uploader
                  #bulkFileUploader
                  allowedExtensions=".csv"
                  maxFileSize="5000000"
                  multiple="false"
                  [asyncSettings]="path"
                  (uploading)="addBulkFileUploadHeaders($event)"
                  (removing)="addBulkFileUploadHeaders($event)"
                  (success)="onBulkFileUploadSuccess($event)"
                />
              </div>
              <div>
                <button
                  class="btn btn-outline-primary"
                  (click)="uploadFileAndInsertTdRates()"
                  [disabled]="bulkUploadForm.invalid"
                  [disabled]="bulkUploadForm.invalid"
                >
                  IMPORT FILE
                </button>
              </div>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <p class="m-0">You can use this template file:</p>
              <button class="btn btn-outline-primary border-0" (click)="downloadTemplate()">
                <i class="bi bi-filetype-csv"></i> tdRates-bulk-upload.template.csv
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="card">
        <div class="card-body p-0">
          @if (banks && fiduciaries) {
            <ejs-grid
              [dataSource]="tdRates$ | async"
              [editSettings]="{
                allowEditing: true,
                allowAdding: true,
                allowDeleting: true,
                mode: 'Normal'
              }"
              [toolbar]="['Add', 'Edit', 'Update', 'Delete', 'Cancel']"
              id="grdTdRates"
              #grdTdRates
              allowPaging="true"
              allowTextWrap="true"
              [pageSettings]="{ pageSizes: true, pageSize: 20 }"
              (actionComplete)="onActionComplete($event)"
            >
              <e-columns>
                <e-column
                  field="id"
                  headerText="#"
                  width="40"
                  [isPrimaryKey]="true"
                  [allowEditing]="false"
                  [visible]="false"
                />
                <e-column
                  field="bankId"
                  headerText="Bank"
                  editType="dropdownedit"
                  [edit]="bankEditParams"
                  [validationRules]="{ required: true }"
                  width="120"
                >
                  <ng-template #template let-data>
                    {{ data.bank?.name }}
                  </ng-template>
                </e-column>
                <e-column field="bank.location" headerText="Bank Location" [allowEditing]="false" />
                <e-column field="bank.creditRating" headerText="Bank Credit Rating" [allowEditing]="false" />
                <e-column
                  field="fiduciaryId"
                  headerText="Fiduciary"
                  editType="dropdownedit"
                  [edit]="fiduciaryEditParams"
                  width="120"
                >
                  <ng-template #template let-data>
                    {{ data.fiduciary?.name }}
                  </ng-template>
                </e-column>
                <e-column field="fiduciary.location" headerText="Fiduciary Location" [allowEditing]="false" />
                <e-column field="fiduciary.creditRating" headerText="Fiduciary Credit Rating" [allowEditing]="false" />
                <e-column field="minNotional" headerText="Min. Notional" editType="stringedit" />
                <e-column
                  field="on"
                  headerText="O/N"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="oneWeek"
                  headerText="1W"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="twoWeeks"
                  headerText="2W"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="oneMonth"
                  headerText="1M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="twoMonths"
                  headerText="2M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="threeMonths"
                  headerText="3M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="fourMonths"
                  headerText="4M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="fiveMonths"
                  headerText="5M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="sixMonths"
                  headerText="6M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="sevenMonths"
                  headerText="7M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="eightMonths"
                  headerText="8M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="nineMonths"
                  headerText="9M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="tenMonths"
                  headerText="10M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="elevenMonths"
                  headerText="11M"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column
                  field="oneYear"
                  headerText="1Y"
                  type="number"
                  format="P2"
                  editType="numericedit"
                  [edit]="rateEditParams"
                />
                <e-column field="created" headerText="Created" width="140" [allowEditing]="false">
                  <ng-template #template let-data>
                    {{ data.created ? (data.created | date: 'dd/MM/yyyy HH:mm') : '-' }}
                  </ng-template>
                </e-column>
                <e-column field="updated" headerText="Updated" width="140" [allowEditing]="false">
                  <ng-template #template let-data>
                    {{ data.updated ? (data.updated | date: 'dd/MM/yyyy HH:mm') : '-' }}
                  </ng-template>
                </e-column>
              </e-columns>
            </ejs-grid>
          }
        </div>
      </div>
    </div>

    <eaa-toast #toastTdRatesAction [showCloseButton]="false">
      <div id="title">PROCESSING</div>
      <div id="content" class="d-flex gap-2">
        <eaa-spinner [diameter]="20" />
        <div>
          {{ processingMessage }}
        </div>
      </div>
    </eaa-toast>
  `
})
export class TdRatesComponent implements OnInit {
  public tdRates$: Observable<TdRate[]> | null = null;
  public processingMessage = '';
  public selectedDate: Date = new Date();

  public currencies = TD_RATES_CURRENCIES;
  public selectedCurrency: string = this.currencies[0];

  public bankEditParams: IEditCell | null = null;
  public banks: TdRateBank[] | null = null;

  public fiduciaryEditParams: IEditCell | null = null;
  public fiduciaries: TdRateFiduciary[] | null = null;

  public bulkUploadForm: FormGroup = new FormGroup({});
  private accessToken: string | null = null;
  public path: UploadPath = {
    saveUrl: `${environment.apiEndpoint}/api/td-rates/bulk/temp-upload`,
    removeUrl: `${environment.apiEndpoint}/api/td-rates/bulk/temp-delete`
  };

  public currencyEditParams?: IEditCell = {
    params: { dataSource: ['EUR', 'USD'], query: new Query(), actionComplete: () => false }
  };

  public rateEditParams?: IEditCell = {
    params: {
      decimals: 4,
      format: 'P2',
      showClearButton: true,
      showSpinButton: false
    }
  };

  @ViewChild('grdTdRates')
  public grid?: GridComponent;

  @ViewChild('toastTdRatesAction')
  public toastTdRatesAction?: ToastComponent;

  @ViewChild('bulkFileUploader')
  public uploader?: UploaderComponent;

  constructor(
    private tdRatesService: TdRatesService,
    private tdRatesBanksService: TdRatesBanksService,
    private tdRatesFiduciariesService: TdRatesFiduciariesService,
    public apiErrorDialog: MatDialog,
    private fb: FormBuilder,
    private authService: EaccessAuthService
  ) {}

  public ngOnInit(): void {
    this.bulkUploadForm = this.fb.group({
      bulkTdRatesFileTempPath: new FormControl<string | null>(null, Validators.required)
    });

    this.authService.getAccessToken$().subscribe((accessToken) => (this.accessToken = accessToken));

    this.loadTdRates();

    this.tdRatesBanksService.getTdRateBanks$().subscribe((banks) => {
      this.banks = banks;
      this.bankEditParams = {
        params: {
          dataSource: new DataManager(banks),
          fields: { text: 'name', value: 'id' },
          query: new Query(),
          actionComplete: () => false
        }
      };

      setTimeout(() => {
        this.grid?.enableToolbarItems(['grdTdRates_add'], this.selectedDate !== null && this.selectedCurrency !== null);
      }, 150);
    });

    this.tdRatesFiduciariesService.getTdRateFiduciaries$().subscribe((fiduciaries) => {
      this.fiduciaries = fiduciaries;
      this.fiduciaryEditParams = {
        params: {
          dataSource: new DataManager(fiduciaries),
          fields: { text: 'name', value: 'id' },
          query: new Query(),
          showClearButton: true,
          actionComplete: () => false
        }
      };
    });
  }

  public onActionComplete = (args: any) => {
    if (args.requestType === 'save') {
      const data = args.data as any;

      if (data.id) {
        // Updating existing TD rate
        const tdRateToUpdate: TdRateToUpdate = {
          minNotional: data.minNotional,
          on: data.on,
          oneWeek: data.oneWeek,
          twoWeeks: data.twoWeeks,
          oneMonth: data.oneMonth,
          twoMonths: data.twoMonths,
          threeMonths: data.threeMonths,
          fourMonths: data.fourMonths,
          fiveMonths: data.fiveMonths,
          sixMonths: data.sixMonths,
          sevenMonths: data.sevenMonths,
          eightMonths: data.eightMonths,
          nineMonths: data.nineMonths,
          tenMonths: data.tenMonths,
          elevenMonths: data.elevenMonths,
          oneYear: data.oneYear,
          fiduciaryId: data.fiduciaryId
        };

        const displayTdRate: TdRate = data;
        this.processingMessage = `Updating TD rate ${this.formatTdRate(displayTdRate)}...`;
        this.toastTdRatesAction?.show();

        this.tdRatesService.updateTdRate$(data.id, tdRateToUpdate).subscribe({
          next: (updated) => this.handleAfterExecute(true, `Updated TD rate ${this.formatTdRate(updated)}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      } else {
        // Adding new TD rate
        if (!this.selectedCurrency) {
          this.resetToast();
          return;
        }

        const tdRateToAdd: TdRateToAdd = {
          minNotional: data.minNotional,
          on: data.on,
          oneWeek: data.oneWeek,
          twoWeeks: data.twoWeeks,
          oneMonth: data.oneMonth,
          twoMonths: data.twoMonths,
          threeMonths: data.threeMonths,
          fourMonths: data.fourMonths,
          fiveMonths: data.fiveMonths,
          sixMonths: data.sixMonths,
          sevenMonths: data.sevenMonths,
          eightMonths: data.eightMonths,
          nineMonths: data.nineMonths,
          tenMonths: data.tenMonths,
          elevenMonths: data.elevenMonths,
          oneYear: data.oneYear,
          fiduciaryId: data.fiduciaryId,
          bankId: data.bankId,
          currency: this.selectedCurrency,
          date: moment(this.selectedDate).format('YYYY-MM-DD')
        };

        const displayTdRate: any = {
          ...tdRateToAdd,
          bank: data.bank
        };
        this.processingMessage = `Adding new TD rate ${this.formatTdRate(displayTdRate)}...`;
        this.toastTdRatesAction?.show();

        this.tdRatesService.insertTdRate$(tdRateToAdd).subscribe({
          next: (inserted) => this.handleAfterExecute(true, `Added new TD rate ${this.formatTdRate(inserted)}`),
          error: (error) => this.handleAfterExecute(false, null, error)
        });
      }
    } else if (args.requestType === 'delete') {
      const tdRates = (args.data as TdRate[]) || [];

      if (tdRates.length === 0) {
        this.resetToast();
        return;
      }

      this.processingMessage = `Deleting TD rates ${tdRates.map((b) => this.formatTdRate(b)).join(', ')}...`;
      this.toastTdRatesAction?.show();

      this.tdRatesService.deleteMultipleTdRates$(tdRates.map((b) => b.id)).subscribe({
        next: () =>
          this.handleAfterExecute(true, `Deleted TD rates ${tdRates.map((b) => this.formatTdRate(b)).join(', ')}`),
        error: (error) => this.handleAfterExecute(false, null, error)
      });
    }
  };

  private handleAfterExecute(
    success: boolean,
    processingMessage: string | null = null,
    error: any | null = null
  ): void {
    if (success) {
      this.loadTdRates();
      this.processingMessage = processingMessage || `Completed.`;

      setTimeout(() => {
        this.resetToast();
      }, 1500);
    } else {
      this.resetToast();

      if (error) {
        this.apiErrorDialog.open(ApiErrorDialogComponent, {
          width: '800px',
          data: error
        });
      }
    }
  }

  private resetToast() {
    this.toastTdRatesAction?.hide();
    this.processingMessage = '';
  }

  private formatTdRate(tdRate: TdRate): string {
    return `${tdRate.date ? moment(tdRate.date).format('YYYY-MM-DD') : 'N/A'} - ${tdRate.currency || 'N/A'} - ${tdRate.bank?.name || tdRate.bankId || 'N/A'}`;
  }

  public onCurrencyChange(newCurrency: string): void {
    this.selectedCurrency = newCurrency;
    this.loadTdRates();
  }

  public onDateChange(newDate: Date): void {
    this.selectedDate = newDate;
    this.loadTdRates();
  }

  private loadTdRates(): void {
    this.tdRates$ = null;
    this.grid?.enableToolbarItems(['grdTdRates_add'], this.selectedDate !== null && this.selectedCurrency !== null);

    if (!this.selectedDate || !this.selectedCurrency) {
      return;
    }

    this.tdRates$ = this.tdRatesService.getTdRates$(this.selectedDate, this.selectedCurrency);
  }

  public downloadTemplate(): void {
    this.tdRatesService.getBulkTdRatesFileTemplate$().subscribe((blob) => {
      saveAs(blob, 'tdrates-bulk-upload.template.csv');
    });
  }

  public uploadFileAndInsertTdRates(): void {
    this.processingMessage = `Adding or updating new TD rates in bulk from file...`;
    this.toastTdRatesAction?.show();

    this.tdRatesService
      .uploadBulkTdRatesFileAndInsertTdRates$(this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.value)
      .subscribe({
        next: (insertedCount) => {
          this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.reset();
          this.uploader?.clearAll();
          this.handleAfterExecute(true, `Inserted or updated ${insertedCount} TD rates from the uploaded file.`);
        },
        error: (error) => this.handleAfterExecute(false, null, error)
      });
  }

  public addBulkFileUploadHeaders(args: any): void {
    args.currentRequest.setRequestHeader('Authorization', `Bearer ${this.accessToken}`);

    if (this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.value) {
      args.currentRequest.setRequestHeader('X-TempPath', this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.value);
    }
  }

  public onBulkFileUploadSuccess(event: any): void {
    if (event.operation === 'upload') {
      const tempPath = JSON.parse(event.e.currentTarget.response).path;

      if (tempPath) {
        this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.setValue(tempPath);
      }
    } else if (event.operation === 'remove') {
      this.bulkUploadForm.get('bulkTdRatesFileTempPath')?.reset();
    }
  }
}
